import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../../components/feature/AdminLayout';
import CostSummaryCard from '../../../../components/feature/CostSummaryCard';
import AllocationSection from './components/AllocationSection';
import OrderLogSection from './components/OrderLogSection';
import PayableSummaryCard from './components/PayableSummaryCard';
import { supabase } from '../../../../services/supabaseClient';
import {
  checkCalendarEventExists,
  syncOrderToCalendar,
  resyncOrderToCalendar,
} from '../../../../modules/calendar';
import {
  calcRentalDays,
  calcOrderCost,
  formatCurrency,
  normalizeOrderItems,
  normalizeCustomer,
} from '../../../../modules/order';
import type {
  OrderItemData,
  CustomerData,
  OrderCostSummary,
} from '../../../../modules/order';
import {
  writeAllocationLog,
  getCurrentAdmin,
} from '../../../../modules/allocation';
import {
  formatProductName,
  formatDailyPrice,
} from '../../../../modules/product';
import {
  processPrepaidPayment,
  checkIsPrepaidMember,
  getPrepaidBalance,
} from '../../../../modules/prepaid';
import StateGovernancePanel from './components/StateGovernancePanel';
import PickupGovernanceChecklist from './components/PickupGovernanceChecklist';
import { IdentityGatingBanner } from './components/IdentityGatingBanner';
import type { IdentityDocumentSummary } from '../../../../modules/identity/types';
import { getCustomerIdentityStatus } from '../../../../services/identityGatewayService';
import ManagerOverrideModal, { type OverrideReason } from './components/ManagerOverrideModal';
import AuditTimeline from './components/AuditTimeline';
import OrderSemanticBanner from './components/OrderSemanticBanner';
import { isHighestPrivilegeAdmin } from '../../../../utils/adminRole';
import { getOrderStatusDisplay } from '../../../../modules/order';
import { isValidUUID, adminOrderContractPath } from '../../../../utils/orderRoutes';

interface Order {
  id: string;
  order_number: string;
  customer_id: string;
  user_id: string;
  start_date: string;
  end_date: string;
  pickup_location_id: string;
  return_location_id: string;
  total_price: number;
  total_deposit: number;
  total_amount: number;
  status: string;
  notes: string;
  vehicle_id?: string;
  vehicle_confirmed?: boolean;
  vehicle_fee?: number;
  created_at: string;
  pickup_location?: {
    id: string;
    name_zh: string;
    name_ja: string;
    address: string;
  };
  return_location?: {
    id: string;
    name_zh: string;
    name_ja: string;
    address: string;
  };
  // ⏰ Phase4a — order time fields
  requested_pickup_time?: string | null;
  requested_return_time?: string | null;
  confirmed_pickup_time?: string | null;
  confirmed_return_time?: string | null;
}

interface Location {
  id: string;
  name_zh: string;
  name_ja: string;
  address: string;
  type: string;
}

// 車輛資訊
interface Vehicle {
  id: string;
  plate_number: string;
  rental_fee: number;
  cost: number;
  status: string;
}

export default function AdminOrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItemData[]>([]);
  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [costSummary, setCostSummary] = useState<OrderCostSummary | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Phase 1: identity gating state
  const [identitySummary, setIdentitySummary] = useState<IdentityDocumentSummary | null>(null);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedOrder, setEditedOrder] = useState<Partial<Order>>();
  const [logRefreshKey, setLogRefreshKey] = useState(0);
  const [calendarEventExists, setCalendarEventExists] = useState(false);
  const [showResignWarning, setShowResignWarning] = useState(false);
  const [showCalendarSyncModal, setShowCalendarSyncModal] = useState(false);
  const [isSyncingCalendar, setIsSyncingCalendar] = useState(false);
  const [hasVehicleRequest, setHasVehicleRequest] = useState(false);
  const [vehicleConfirmed, setVehicleConfirmed] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('');
  const [isPrepaidMember, setIsPrepaidMember] = useState(false);
  const [prepaidBalance, setPrepaidBalance] = useState<{
    realBalance: number;
    bonusBalance: number;
    totalBalance: number;
    status: string;
  } | null>(null);
  const [showPrepaidPaymentModal, setShowPrepaidPaymentModal] = useState(false);
  const [isProcessingPrepaid, setIsProcessingPrepaid] = useState(false);
  const [showPaymentChoiceModal, setShowPaymentChoiceModal] = useState(false);

  // Phase1078A: order state governance hooks
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideScenario, setOverrideScenario] = useState<'early-pickup' | 'rollback' | 'mistaken-pickup'>('early-pickup');
  const [pendingStatusChange, setPendingStatusChange] = useState<string | null>(null);
  const [adminRole, setAdminRole] = useState<string | null>(null);
  const overrideBypassRef = useRef(false);

  // 計算 Urent 品項數量與是否有 Urent 品項（只宣告一次）
  const urentItemsCount = useMemo(
    () => orderItems.filter((item) => item.is_urent_item).length,
    [orderItems],
  );
  const hasUrentItems = useMemo(
    () => orderItems.some((item) => item.is_urent_item),
    [orderItems],
  );

  // 取得已選擇的車輛
  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v.id === selectedVehicleId) || null,
    [vehicles, selectedVehicleId],
  );

  // Derived booleans for action gating
  const allItemsAllocated = useMemo(
    () => orderItems.length > 0 && orderItems.every((item) => item.equipment_item_id || item.is_external_dispatch),
    [orderItems],
  );

  const vehicleReady = useMemo(
    () => !hasVehicleRequest || !vehicleConfirmed || !!order?.vehicle_id,
    [hasVehicleRequest, vehicleConfirmed, order?.vehicle_id],
  );

  // Phase 1: identityReady = summary.identityReady (direct from gateway)
  const identityReady = identitySummary?.identityReady === true;

  const canAcceptOrder = order?.status === 'pending' && allItemsAllocated && vehicleReady && identityReady;
  const canGenerateContract = order?.status === 'approved' && allItemsAllocated && vehicleReady && identityReady;

  useEffect(() => {
    if (orderId && isValidUUID(orderId)) {
      fetchOrderDetail();
      checkExistingCalendarEvent();
    } else {
      setLoadError('無效的訂單 ID');
      setLoading(false);
    }
    // Phase1078A: load admin role for transition restrictions
    const storedRole = localStorage.getItem('adminRole');
    if (storedRole) setAdminRole(storedRole);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const checkExistingCalendarEvent = async () => {
    if (!orderId || !isValidUUID(orderId)) return;
    const { exists } = await checkCalendarEventExists(orderId);
    setCalendarEventExists(exists);
  };

  const fetchOrderDetail = async () => {
    if (!orderId || !isValidUUID(orderId)) {
      const msg = orderId === ':orderId'
        ? '訂單 ID 格式不正確（路由參數未解析）'
        : '無效的訂單 ID';
      setLoadError(msg);
      setLoading(false);
      return;
    }
    setLoadError(null);
    setLoading(true);
    try {
      // 1️⃣ 載入門市
      const { data: locationsData } = await supabase
        .from('locations')
        .select('id, name_zh, name_ja, address, type')
        .order('name_zh');
      if (locationsData) setLocations(locationsData);

      // 2️⃣ 載入車輛
      const { data: vehiclesData } = await supabase
        .from('vehicles')
        .select('id, plate_number, rental_fee, cost, status')
        .order('plate_number');
      if (vehiclesData) setVehicles(vehiclesData);

      // 3️⃣ 讀取訂單本身
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select(`
          *,
          pickup_location:locations!orders_pickup_location_id_fkey (id, name_zh, name_ja, address),
          return_location:locations!orders_return_location_id_fkey (id, name_zh, name_ja, address)
        `)
        .eq('id', orderId)
        .maybeSingle();

      if (orderError) throw orderError;
      if (!orderData) {
        alert('找不到此訂單');
        navigate('/admin/orders');
        return;
      }

      setOrder(orderData);
      setEditedOrder(orderData);

      // ✅ 修正：初始化車輛相關 state（之前遺漏，導致 hasVehicleRequest 永遠 false）
      setHasVehicleRequest(!!orderData.vehicle_included);
      setVehicleConfirmed(orderData.vehicle_confirmed || false);
      setSelectedVehicleId(orderData.vehicle_id || '');

      // 4️⃣ 讀取客戶
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .eq('id', orderData.customer_id)
        .maybeSingle();
      if (customerError) throw customerError;
      setCustomer(customerData ? normalizeCustomer(customerData) : null);

      // Phase 2: 讀取 Railway identity gateway status
      if (customerData?.id) {
        try {
          const gatewayStatus = await getCustomerIdentityStatus(customerData.id);
          setIdentitySummary(gatewayStatus);
          setIdentityError(null);
        } catch {
          setIdentityError('身分驗證狀態暫時無法確認');
          setIdentitySummary(null);
        }
      }

      // 5️⃣ 判斷是否為 Urent 會員及載入餘額
      if (orderData.customer_id) {
        const isMember = await checkIsPrepaidMember(orderData.customer_id);
        setIsPrepaidMember(isMember);
        if (isMember) {
          const balance = await getPrepaidBalance(orderData.customer_id);
          setPrepaidBalance(balance);
        }
      }

      // 6️⃣ 讀取訂單項目
      const { data: itemsData, error: itemsError } = await supabase
        .from('order_items')
        .select(`
          *,
          products:product_id (
            id, name_zh, name_ja, name_en, category, brand, daily_price, deposit, image_urls
          ),
          equipment_items:equipment_item_id (
            id, serial_number, status,
            locations:location_id ( name_zh )
          )
        `)
        .eq('order_id', orderId);
      if (itemsError) throw itemsError;
      const normalizedItems = normalizeOrderItems(itemsData || []);
      setOrderItems(normalizedItems);

      // 7️⃣ 計算費用摘要
      const days = calcRentalDays(orderData.start_date, orderData.end_date);
      // ✅ 修正：直接使用原始折扣率，不要除以 100
      const discountRate = customerData?.discount ?? 100;
      // ✅ 修正：使用 local 變數 vehicleIncluded，避免讀到 stale 的 hasVehicleRequest state
      const vehicleIncluded = !!orderData.vehicle_included;
      const vehicleFee =
        vehicleIncluded && orderData.vehicle_confirmed && orderData.vehicle_fee
          ? orderData.vehicle_fee
          : undefined;
      const summary = calcOrderCost(normalizedItems, days, discountRate, vehicleFee);
      setCostSummary(summary);
    } catch (error) {
      console.error('載入訂單詳情失敗:', error);
      alert('載入訂單詳情失敗');
    } finally {
      setLoading(false);
    }
  };

  // ----------------------- 事件處理 -----------------------
  const handleEdit = () => {
    setIsEditing(true);
    setEditedOrder(order || {});
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedOrder(order || {});
    setVehicleConfirmed(order?.vehicle_confirmed || false);
    setSelectedVehicleId(order?.vehicle_id || '');
  };

  const handleSaveEdit = async () => {
    if (!order) return;
    try {
      const oldStatus = order.status;
      const newStatus = editedOrder.status!;
      const oldVehicleConfirmed = order.vehicle_confirmed || false;
      const oldVehicleId = order.vehicle_id || '';

      // Phase1078A: premature pickup guard — cannot mark renting before pickup_date
      if (newStatus === 'renting' && !overrideBypassRef.current) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const pickupDate = new Date(order.start_date);
        pickupDate.setHours(0, 0, 0, 0);
        if (today < pickupDate) {
          setOverrideScenario('early-pickup');
          setShowOverrideModal(true);
          setPendingStatusChange(newStatus);
          return;
        }
      }

      // Phase1078A: restricted transition guard — rollback from active rental needs override
      if (oldStatus === 'renting' && ['pending', 'approved', 'draft'].includes(newStatus) && !overrideBypassRef.current) {
        if (!isHighestPrivilegeAdmin(adminRole) && adminRole !== 'store_manager') {
          alert('⚠️ 此訂單已進入風險移轉階段，僅店長以上可回退狀態。');
          return;
        }
        setOverrideScenario('rollback');
        setShowOverrideModal(true);
        setPendingStatusChange(newStatus);
        return;
      }

      // 清除 bypass 標記（已通過 guard）
      overrideBypassRef.current = false;

      // 若改為已核准，必須所有器材已分配
      if (editedOrder.status === 'approved') {
        const allAllocated = orderItems.every((item) => item.equipment_item_id || item.is_external_dispatch);
        if (!allAllocated) {
          alert('⚠️ 請先完成所有器材分配，才能將訂單狀態改為「已核准」');
          return;
        }
      }

      // 若有加租車輛需求，必須先選擇車輛
      if (hasVehicleRequest && vehicleConfirmed && !selectedVehicleId) {
        alert('⚠️ 請選擇要指派的車輛');
        return;
      }

      // ---------- 更新資料 ----------
      const updateData: any = {
        status: newStatus,
        notes: editedOrder.notes,
        start_date: editedOrder.start_date,
        end_date: editedOrder.end_date,
        pickup_location_id: editedOrder.pickup_location_id,
        return_location_id: editedOrder.return_location_id,
      };

      if (hasVehicleRequest) {
        updateData.vehicle_confirmed = vehicleConfirmed;
        updateData.vehicle_id = vehicleConfirmed ? selectedVehicleId : null;
        if (vehicleConfirmed && selectedVehicle) {
          const days = calcRentalDays(
            editedOrder.start_date || order.start_date,
            editedOrder.end_date || order.end_date,
          );
          updateData.vehicle_fee = selectedVehicle.rental_fee * days;
        } else {
          updateData.vehicle_fee = 0;
        }
      }

      const { error } = await supabase.from('orders').update(updateData).eq('id', orderId);
      if (error) throw error;

      // ---------- Urent 相關流程 ----------
      // ① 若狀態從 renting 變為非 renting，還原 Urent 狀態
      if (oldStatus === 'renting' && newStatus !== 'renting' && newStatus !== 'completed') {
        await handleRevertUrentStatus();
      }

      // ② 若狀態變為 renting，計算 Urent 收入並更新狀態
      if (oldStatus !== 'renting' && newStatus === 'renting') {
        await handleCalculateUrentIncome();
      }

      // ③ 若狀態變為 completed，處理歸還
      if (oldStatus !== 'completed' && newStatus === 'completed') {
        await handleOrderCompleted();
      }

      // ---------- 紀錄 ----------
      const admin = await getCurrentAdmin();
      if (admin) {
        const changes: string[] = [];
        if (editedOrder.status && editedOrder.status !== order.status) {
          changes.push(`狀態：${getStatusText(order.status)} → ${getStatusText(editedOrder.status)}`);
        }
        if (editedOrder.start_date && editedOrder.start_date !== order.start_date) {
          changes.push(`開始日期：${order.start_date} → ${editedOrder.start_date}`);
        }
        if (editedOrder.end_date && editedOrder.end_date !== order.end_date) {
          changes.push(`結束日期：${order.end_date} → ${editedOrder.end_date}`);
        }
        if (editedOrder.pickup_location_id && editedOrder.pickup_location_id !== order.pickup_location_id) {
          const oldLoc = locations.find((l) => l.id === order.pickup_location_id);
          const newLoc = locations.find((l) => l.id === editedOrder.pickup_location_id);
          changes.push(`取件門市：${oldLoc?.name_zh || '未知'} → ${newLoc?.name_zh || '未知'}`);
        }
        if (editedOrder.return_location_id && editedOrder.return_location_id !== order.return_location_id) {
          const oldLoc = locations.find((l) => l.id === order.return_location_id);
          const newLoc = locations.find((l) => l.id === editedOrder.return_location_id);
          changes.push(`還車門市：${oldLoc?.name_zh || '未知'} → ${newLoc?.name_zh || '未知'}`);
        }
        if (editedOrder.notes && editedOrder.notes !== order.notes) {
          changes.push('備註已修改');
        }
        if (hasVehicleRequest) {
          if (vehicleConfirmed !== oldVehicleConfirmed) {
            changes.push(`加租車輛：${oldVehicleConfirmed ? '已確認' : '未確認'} → ${vehicleConfirmed ? '已確認' : '未確認'}`);
          }
          if (selectedVehicleId !== oldVehicleId && vehicleConfirmed) {
            const oldVeh = vehicles.find((v) => v.id === oldVehicleId);
            const newVeh = vehicles.find((v) => v.id === selectedVehicleId);
            changes.push(`指派車輛：${oldVeh?.plate_number || '無'} → ${newVeh?.plate_number || '無'}`);
          }
        }

        if (changes.length > 0) {
          await writeAllocationLog({
            orderId: order.id,
            actionType: 'order_edit',
            description: `編輯訂單：${changes.join('、')}`,
            adminId: admin.id,
            adminName: admin.name,
          });
          setLogRefreshKey((k) => k + 1);
        }
      }

      alert('✅ 訂單更新成功！');
      setIsEditing(false);
      await fetchOrderDetail();
    } catch (error) {
      console.error('更新訂單失敗:', error);
      alert('更新訂單失敗');
    }
  };

  // ----------------------- Urent 流程 -----------------------
  /**
   * 🆕 將 Urent 品項狀態改回閒置 - 當訂單從「出租中」改回其他狀態時觸發
   */
  const handleRevertUrentStatus = async () => {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🔄 開始還原 Urent 品項狀態');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    // ✅ 修正：使用正確欄位 is_urent_item
    const urentItems = orderItems.filter(item => item.is_urent_item && item.equipment_item_id);
    
    console.log('📦 Urent 品項檢查:', {
      totalOrderItems: orderItems.length,
      urentItemsCount: orderItems.filter(item => item.is_urent_item).length,
      allocatedUrentItemsCount: urentItems.length,
    });

    if (urentItems.length === 0) {
      console.log('⏭️ 此訂單沒有已分配的 Urent 品項，跳過狀態還原');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return;
    }

    const admin = await getCurrentAdmin();
    const results: Array<{
      success: boolean;
      itemId: string;
      serialNumber: string;
      error?: string;
    }> = [];

    for (const item of urentItems) {
      const serial = item.equipment_item?.serial_number || item.equipment_item_id!;
      try {
        const { data, error: queryErr } = await supabase
          .from('equipment_items')
          .select('id, urent_status')
          .eq('id', item.equipment_item_id!)
          .maybeSingle();
        if (queryErr) throw queryErr;

        const { error: updErr } = await supabase
          .from('equipment_items')
          .update({ urent_status: 'idle' })
          .eq('id', item.equipment_item_id!);
        if (updErr) throw updErr;

        // ✅ 修正：使用正確的欄位名稱 item_id
        await supabase.from('equipment_item_history').insert({
          item_id: item.equipment_item_id,
          action_type: 'urent_status_reverted',
          field_changed: 'urent_status',
          old_value: 'rented',
          new_value: 'idle',
          related_order_id: order?.id,
          created_by: admin?.name || '系統',
          notes: `訂單 ${order?.order_number} 狀態變更，Urent 品項回復閒置`,
        });

        results.push({ success: true, itemId: item.equipment_item_id!, serialNumber: serial });
      } catch (e: any) {
        console.error(`❌ 還原 Urent 品項 ${serial} 失敗:`, e);
        results.push({ success: false, itemId: item.equipment_item_id!, serialNumber: serial, error: e.message });
      }
    }

    const successCount = results.filter((r) => r.success).length;
    console.log(`✅ Urent 品項狀態還原完成: ${successCount}/${urentItems.length} 件成功`);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    if (admin && successCount > 0) {
      await writeAllocationLog({
        orderId: order!.id,
        actionType: 'urent_status_reverted',
        description: `Urent 品項回復閒置 ${successCount} 件`,
        adminId: admin.id,
        adminName: admin.name,
      });
      setLogRefreshKey((k) => k + 1);
    }
  };

  /**
   * 🆕 處理訂單歸還 - 將庫存品項狀態改為可預約，並更新位置到歸還門市
   */
  const handleOrderCompleted = async () => {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📦 開始處理訂單歸還');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    const returnLocationId = editedOrder.return_location_id || order?.return_location_id;
    const admin = await getCurrentAdmin();
    
    const inventoryItems = orderItems.filter(
      item => item.equipment_item_id && !item.is_external_dispatch
    );

    console.log('📊 品項統計:', {
      totalItems: orderItems.length,
      inventoryItems: inventoryItems.length,
      externalDispatchItems: orderItems.filter(item => item.is_external_dispatch).length,
      // ✅ 修正：使用正確欄位 is_urent_item
      urentItems: inventoryItems.filter(item => item.is_urent_item).length,
    });

    if (inventoryItems.length === 0) {
      console.log('⏭️ 此訂單沒有需要歸還的庫存品項');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return;
    }

    const results: Array<{
      success: boolean;
      itemId: string;
      serialNumber: string;
      isUrent: boolean;
      error?: string;
    }> = [];

    const returnLocation = locations.find((l) => l.id === returnLocationId);
    const returnLocationName = returnLocation?.name_zh || '未指定';

    for (const item of inventoryItems) {
      const serial = item.equipment_item?.serial_number || item.equipment_item_id!;
      // ✅ 修正：使用正確欄位 is_urent_item
      const isUrent = !!item.is_urent_item;
      try {
        const { error: updErr } = await supabase
          .from('equipment_items')
          .update({
            status: 'available',
            location_id: returnLocationId,
            ...(isUrent && { urent_status: 'idle' }),
          })
          .eq('id', item.equipment_item_id!);
        if (updErr) throw updErr;

        // ✅ 修正：使用正確的欄位名稱 item_id
        await supabase.from('equipment_item_history').insert({
          item_id: item.equipment_item_id,
          action_type: 'order_returned',
          field_changed: isUrent ? 'status,location_id,urent_status' : 'status,location_id',
          old_value: JSON.stringify({
            status: 'rented',
            location: item.equipment_item?.location_name_zh || '出租中',
            ...(isUrent && { urent_status: 'rented' }),
          }),
          new_value: JSON.stringify({
            status: 'available',
            location: returnLocationName,
            ...(isUrent && { urent_status: 'idle' }),
          }),
          old_location_id: item.equipment_item?.location_id,
          new_location_id: returnLocationId,
          related_order_id: order?.id,
          created_by: admin?.name || '系統',
          notes: `訂單 ${order?.order_number} 歸還，${isUrent ? 'Urent' : '普通'} 品項回復可租`,
        });

        results.push({ success: true, itemId: item.equipment_item_id!, serialNumber: serial, isUrent });
        console.log(`✅ 品項 ${serial} 歸還成功 (${isUrent ? 'Urent' : '普通'})`);
      } catch (e: any) {
        console.error(`❌ 品項 ${serial} 歸還失敗:`, e);
        results.push({ success: false, itemId: item.equipment_item_id!, serialNumber: serial, isUrent, error: e.message });
      }
    }

    const successCount = results.filter((r) => r.success).length;
    const urentCount = results.filter((r) => r.success && r.isUrent).length;

    console.log(`✅ 訂單歸還完成: ${successCount}/${inventoryItems.length} 件成功 (含 ${urentCount} 件 Urent)`);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    if (admin && successCount > 0) {
      const desc = urentCount > 0
        ? `訂單歸還完成，${successCount} 件品項（含 ${urentCount} 件 Urent）已回到門市 ${returnLocationName}`
        : `訂單歸還完成，${successCount} 件品項已回到門市 ${returnLocationName}`;
      await writeAllocationLog({
        orderId: order!.id,
        actionType: 'order_completed',
        description: desc,
        adminId: admin.id,
        adminName: admin.name,
      });
      setLogRefreshKey((k) => k + 1);
    }
  };

  /**
   * 🆕 計算 Urent 收入 - 當訂單狀態變更為「出租中」時自動觸發
   */
  const handleCalculateUrentIncome = async () => {
    if (!order) return;

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('💰 開始計算 Urent 收入並更新器材狀態');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    // ✅ 修正：使用正確欄位 is_urent_item
    const urentItems = orderItems.filter(item => item.is_urent_item && item.equipment_item_id);
    
    console.log('📦 Urent 品項檢查:', {
      totalOrderItems: orderItems.length,
      urentItemsCount: orderItems.filter(item => item.is_urent_item).length,
      allocatedUrentItemsCount: urentItems.length,
    });

    if (urentItems.length === 0) {
      console.log('⏭️ 此訂單沒有 Urent 品項，跳過收入計算');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return;
    }

    // 先更新 Urent 狀態為 rented
    const statusResults: Array<{
      success: boolean;
      itemId: string;
      serialNumber: string;
      error?: string;
    }> = [];

    for (const item of urentItems) {
      const serial = item.equipment_item?.serial_number || item.equipment_item_id!;
      try {
        const { error: updErr } = await supabase
          .from('equipment_items')
          .update({ urent_status: 'rented' })
          .eq('id', item.equipment_item_id!);
        if (updErr) throw updErr;
        statusResults.push({ success: true, itemId: item.equipment_item_id!, serialNumber: serial });
        console.log(`✅ Urent 品項 ${serial} 狀態更新為 rented`);
      } catch (e: any) {
        console.error(`❌ Urent 品項 ${serial} 狀態更新失敗:`, e);
        statusResults.push({ success: false, itemId: item.equipment_item_id!, serialNumber: serial, error: e.message });
      }
    }

    // 呼叫 Edge Function 計算收入
    const adminId = localStorage.getItem('adminId') ?? undefined;
    const resp = await fetch(
      `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/calculate-urent-income`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ order_id: order.id, performed_by: adminId }),
      },
    );
    const result = await resp.json();

    if (result.success) {
      console.log(`✅ Urent 收入計算成功: ${result.processed_count} 件`);
      const admin = await getCurrentAdmin();
      if (admin) {
        const details = result.results
          .map((r: any) => `${r.product_name}: NT$ ${r.income_amount.toLocaleString()}`)
          .join('、');
        await writeAllocationLog({
          orderId: order.id,
          actionType: 'urent_income_calculated',
          description: `Urent 收入計算完成 (${result.processed_count} 件)：${details}`,
          adminId: admin.id,
          adminName: admin.name,
        });
        setLogRefreshKey((k) => k + 1);
      }
    } else {
      console.error('❌ Urent 收入計算失敗:', result.error);
    }
    
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  };

  // Phase1078A: manager override handler (mock — records reason then proceeds)
  const handleOverrideConfirm = async (reason: OverrideReason) => {
    setShowOverrideModal(false);
    if (!pendingStatusChange || !order) {
      setPendingStatusChange(null);
      return;
    }

    console.log('Phase1078A: Manager override recorded (mock)', {
      orderId: order.id,
      fromStatus: order.status,
      toStatus: pendingStatusChange,
      reason,
    });

    // 設置 bypass 並重新執行保存
    overrideBypassRef.current = true;
    setEditedOrder((prev) => ({ ...prev, status: pendingStatusChange }));
    setTimeout(() => {
      handleSaveEdit();
    }, 0);
    setPendingStatusChange(null);
  };

  // ----------------------- 確認接單 -----------------------
  const handleAcceptOrder = async () => {
    if (!order || !orderId) return;
    if (!allItemsAllocated) {
      alert('⚠️ 請先完成所有器材分配');
      return;
    }
    if (!vehicleReady) {
      alert('⚠️ 請先確認車輛指派');
      return;
    }

    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: 'approved' })
        .eq('id', orderId);
      if (error) throw error;

      const admin = await getCurrentAdmin();
      await supabase.from('order_logs').insert({
        order_id: order.id,
        action_type: 'status_change',
        description: `訂單狀態：${getStatusText(order.status)} → ${getStatusText('approved')}`,
        admin_id: admin?.id,
        admin_name: admin?.name,
      });
      setLogRefreshKey((k) => k + 1);

      alert('✅ 訂單已確認接單');
      await fetchOrderDetail();
      await handleGenerateContract();
    } catch (error) {
      console.error('確認接單失敗:', error);
      alert('確認接單失敗');
    }
  };

  // ----------------------- 其他 UI 事件 -----------------------
  const handleAllocationChange = async () => {
    await fetchOrderDetail();
    setLogRefreshKey((k) => k + 1);
  };

  const handleGenerateContract = async () => {
    if (!order || !customer) return;
    if (isPrepaidMember && !order.prepaid_used) {
      setShowPaymentChoiceModal(true);
      return;
    }
    await proceedToContract();
  };

  const proceedToContract = async () => {
    if (!order) return;
    if (calendarEventExists) {
      setShowCalendarSyncModal(true);
    } else {
      await syncCalendarAndNavigate(false);
    }
  };

  const handleChoosePrepaidPayment = async () => {
    if (!order || !prepaidBalance) return;
    const required = order.total_price || 0;
    if (prepaidBalance.totalBalance < required) {
      const shortage = required - prepaidBalance.totalBalance;
      alert(
        `⚠️ 客戶儲值金不足！\\n\\n需要金額：NT$ ${required.toLocaleString()}\\n目前餘額：NT$ ${prepaidBalance.totalBalance.toLocaleString()}\\n不足金額：NT$ ${shortage.toLocaleString()}\\n\\n將改為現場付款方式。`,
      );
      setShowPaymentChoiceModal(false);
      await proceedToContract();
      return;
    }

    setIsProcessingPrepaid(true);
    try {
      const adminId = localStorage.getItem('adminId') || undefined;
      const adminName = localStorage.getItem('adminName') || undefined;
      const result = await processPrepaidPayment(order.id, adminId, adminName);
      if (result.success) {
        alert(
          `✅ 儲值金扣款成功！\\n\\n扣款金額：NT$ ${result.data?.payment_breakdown.total_amount.toLocaleString()}\\n剩餘餘額：NT$ ${result.data?.balance_info.after.total.toLocaleString()}\\n\\n即將進入合約簽署頁面...`,
        );
        await fetchOrderDetail();
        setShowPrepaidPaymentModal(false);
        await proceedToContract();
      } else {
        alert(`❌ 儲值金扣款失敗：${result.message}\\n\\n請改用現場付款方式。`);
        setShowPrepaidPaymentModal(false);
        await proceedToContract();
      }
    } catch (e) {
      console.error('儲值金扣款失敗:', e);
      alert('儲值金扣款失敗，請改用現場付款方式。');
      setShowPrepaidPaymentModal(false);
      await proceedToContract();
    } finally {
      setIsProcessingPrepaid(false);
    }
  };

  const handleChooseCashPayment = async () => {
    setShowPaymentChoiceModal(false);
    await proceedToContract();
  };

  const syncCalendarAndNavigate = async (isResync: boolean) => {
    if (!order || !customer) return;
    setIsSyncingCalendar(true);
    try {
      const adminName = localStorage.getItem('adminName') || '管理員';
      const assignedVehicle = order.vehicle_id ? vehicles.find((v) => v.id === order.vehicle_id) : null;

      const calendarParams = {
        orderId: order.id,
        orderNumber: order.order_number,
        customerName: customer.name,
        startDate: order.start_date,
        endDate: order.end_date,
        pickupLocationId: order.pickup_location_id,
        pickupLocationName: order.pickup_location?.name_zh || '',
        returnLocationId: order.return_location_id,
        returnLocationName: order.return_location?.name_zh || '',
        requestedPickupTime: order.requested_pickup_time,
        requestedReturnTime: order.requested_return_time,
        confirmedPickupTime: order.confirmed_pickup_time,
        confirmedReturnTime: order.confirmed_return_time,
        orderItems: orderItems,
        adminName,
        hasVehicle: hasVehicleRequest && order.vehicle_confirmed,
        vehiclePlateNumber: assignedVehicle?.plate_number,
      };

      const calendarResult = isResync
        ? await resyncOrderToCalendar(calendarParams)
        : await syncOrderToCalendar(calendarParams);

      if (calendarResult.success) {
        await supabase.from('order_logs').insert({
          order_id: order.id,
          action_type: 'calendar_synced',
          description: `${isResync ? '【重新同步】' : ''}${calendarResult.message}`,
          admin_id: localStorage.getItem('adminId'),
          admin_name: adminName,
        });
        setLogRefreshKey((k) => k + 1);
      }

      alert(
        calendarResult.success
          ? `✅ ${calendarResult.message}\\n\\n即將前往合約頁面...`
          : `⚠️ ${calendarResult.message}\\n\\n仍可繼續產生合約，但建議檢查 Google Calendar 設定。`,
      );
      navigate(adminOrderContractPath(order.id) ?? '/admin/orders');
    } catch (e) {
      console.error('同步日曆失敗:', e);
      alert('⚠️ 同步日曆時發生錯誤，但仍可繼續產生合約。');
      navigate(adminOrderContractPath(order.id) ?? '/admin/orders');
    } finally {
      setIsSyncingCalendar(false);
      setShowCalendarSyncModal(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    const map: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-amber-100 text-amber-800',
      need_info: 'bg-orange-100 text-orange-800',
      approved: 'bg-sky-100 text-sky-800',
      renting: 'bg-emerald-100 text-emerald-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-gray-100 text-gray-800',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  // Phase1104: use canonical semantic display helper
  const getStatusText = (status: string) => getOrderStatusDisplay(status).adminLabel;

  // ----------------------- 渲染 -----------------------
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">載入中...</div>
        </div>
      </AdminLayout>
    );
  }

  if (loadError) {
    return (
      <AdminLayout>
        <div className="px-6 py-8">
          <button
            onClick={() => navigate('/admin/orders')}
            className="text-teal-600 hover:text-teal-800 mb-4 flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回訂單列表
          </button>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl text-red-500"></i>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">無法載入訂單</h2>
            <p className="text-gray-500">{loadError}</p>
            {orderId === ':orderId' && (
              <p className="text-sm text-gray-400 mt-4">
                偵測到路由參數未正確解析，請確認網址格式為 /admin/orders/{'<訂單ID>'}
              </p>
            )}
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!order) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">找不到訂單</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="px-6 py-8">
        {/* 標題區 */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/admin/orders')}
              className="text-teal-600 hover:text-teal-800 mb-2 flex items-center gap-2 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回訂單列表
            </button>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">訂單詳情</h1>
            <p className="text-gray-600">訂單編號：{order.order_number}</p>
            <div className="mt-4 max-w-2xl space-y-3">
              {/* Phase 1: Identity gating banner */}
              {customer && (
                <IdentityGatingBanner
                  summary={identitySummary}
                  customerId={customer.id || order.customer_id}
                  customerName={customer.name || '客戶'}
                  error={identityError}
                />
              )}
              {/* Phase1104: Semantic compatibility layer — canonical state + warnings */}
              <OrderSemanticBanner status={order.status} />
              {/* Phase1078A: State governance phase + legal risk indicators */}
              <StateGovernancePanel
                status={order.status}
                startDate={order.start_date}
                endDate={order.end_date}
                notes={order.notes}
                adminRole={adminRole}
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusBadgeClass(order.status)}`}>
              {getStatusText(order.status)}
            </span>

            {/* Urent 會員標示 */}
            {isPrepaidMember && (
              <div className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium flex items-center gap-1.5">
                <i className="ri-vip-crown-line"></i>
                儲值會員
              </div>
            )}

            {/* 使用儲值金付款按鈕 */}
            {order.status === 'approved' && isPrepaidMember && !order.prepaid_used && (
              <button
                onClick={() => {
                  if (!prepaidBalance || prepaidBalance.totalBalance < (order.total_price || 0)) {
                    const shortage = (order.total_price || 0) - (prepaidBalance?.totalBalance || 0);
                    alert(`⚠️ 客戶儲值金不足！\\n\\n需要金額：NT$ ${(order.total_price || 0).toLocaleString()}\\n目前餘額：NT$ ${(prepaidBalance?.totalBalance || 0).toLocaleString()}\\n不足金額：NT$ ${shortage.toLocaleString()}\\n\\n請改用現場付款方式。`);
                    return;
                  }
                  setShowPrepaidPaymentModal(true);
                }}
                className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-wallet-3-line mr-2"></i>
                使用儲值金付款
              </button>
            )}

            {/* 已使用儲值金標示 */}
            {order.prepaid_used && (
              <div className="px-4 py-2 bg-purple-100 text-purple-800 rounded-lg flex items-center gap-2">
                <i className="ri-checkbox-circle-fill"></i>
                <span className="font-semibold">已使用儲值金</span>
              </div>
            )}

            {/* Phase 1: 身分未驗證時的 gating 提示（僅在確實未驗證時顯示，API 錯誤時由 banner 處理） */}
            {order.status === 'pending' && !identityReady && !identityError && (
              <div className="px-4 py-2 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2 border border-red-200 whitespace-nowrap">
                <i className="ri-shield-user-line"></i>
                請先完成身分證件上傳與驗證
              </div>
            )}

            {/* 確認接單 CTA */}
            {canAcceptOrder && (
              <button
                onClick={handleAcceptOrder}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-checkbox-circle-line mr-2"></i>
                確認接單並產生合約
              </button>
            )}

            {/* 合約相關按鈕 */}
            {canGenerateContract && (
              <>
                <button
                  onClick={() => navigate(adminOrderContractPath(order.id, 'mode=preview-only') ?? '/admin/orders')}
                  className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-eye-line mr-2"></i>
                  合約預覽
                </button>
                <button
                  onClick={handleGenerateContract}
                  disabled={isSyncingCalendar}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSyncingCalendar ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      同步中...
                    </>
                  ) : (
                    <>
                      <i className="ri-file-text-line mr-2"></i>
                      產生合約
                    </>
                  )}
                </button>
              </>
            )}

            {/* 已核准但配貨未完成 - 禁用合約產生提示 */}
            {order.status === 'approved' && !canGenerateContract && (
              <div className="px-4 py-2 bg-gray-100 text-gray-500 rounded-lg text-sm flex items-center gap-2">
                <i className="ri-lock-line"></i>
                請先完成所有器材分配
              </div>
            )}



            {/* 編輯/儲存/取消 按鈕 */}
            {!isEditing ? (
              <button
                onClick={handleEdit}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-edit-line mr-2"></i>
                編輯訂單
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleSaveEdit}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-save-line mr-2"></i>
                  儲存
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  取消
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ------------------- Modal: 儲值金付款 ------------------- */}
        {showPrepaidPaymentModal && prepaidBalance && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <i className="ri-wallet-3-line text-2xl text-purple-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">使用儲值金付款</h3>
                  <p className="text-sm text-gray-500">客戶為儲值會員</p>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">訂單總金額</span>
                  <span className="text-xl font-bold text-gray-900">
                    NT$ {(order.total_price || 0).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 mb-4">
                <div className="text-sm text-purple-700 mb-2 font-medium">客戶儲值餘額</div>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="text-sm text-purple-600">
                      實體：NT$ {prepaidBalance.realBalance.toLocaleString()}
                    </div>
                    <div className="text-sm text-purple-600">
                      贈送：NT$ {prepaidBalance.bonusBalance.toLocaleString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-purple-900">
                      NT$ {prepaidBalance.totalBalance.toLocaleString()}
                    </div>
                    {prepaidBalance.totalBalance >= (order.total_price || 0) ? (
                      <div className="text-xs text-green-600 flex items-center gap-1 justify-end mt-1">
                        <i className="ri-checkbox-circle-fill"></i>
                        餘額充足
                      </div>
                    ) : (
                      <div className="text-xs text-red-600 flex items-center gap-1 justify-end mt-1">
                        <i className="ri-error-warning-fill"></i>
                        餘額不足 NT$ {((order.total_price || 0) - prepaidBalance.totalBalance).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleChoosePrepaidPayment}
                  disabled={isProcessingPrepaid || prepaidBalance.totalBalance < (order.total_price || 0)}
                  className={`w-full p-4 rounded-lg border-2 transition-all cursor-pointer ${
                    prepaidBalance.totalBalance >= (order.total_price || 0)
                      ? 'border-purple-300 bg-purple-50 hover:bg-purple-100 hover:border-purple-400'
                      : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        prepaidBalance.totalBalance >= (order.total_price || 0) ? 'bg-purple-200' : 'bg-gray-200'
                      }`}
                    >
                      <i
                        className={`ri-wallet-3-line text-xl ${
                          prepaidBalance.totalBalance >= (order.total_price || 0) ? 'text-purple-600' : 'text-gray-400'
                        }`}
                      ></i>
                    </div>
                    <div className="flex-1 text-left">
                      <div
                        className={`font-semibold ${
                          prepaidBalance.totalBalance >= (order.total_price || 0) ? 'text-purple-900' : 'text-gray-400'
                        }`}
                      >
                        {isProcessingPrepaid ? '處理中...' : '使用儲值金付款'}
                      </div>
                      <div
                        className={`text-sm ${
                          prepaidBalance.totalBalance >= (order.total_price || 0) ? 'text-purple-600' : 'text-gray-400'
                        }`}
                      >
                        {prepaidBalance.totalBalance >= (order.total_price || 0)
                          ? '立即從儲值帳戶扣款'
                          : '餘額不足，無法使用'}
                      </div>
                    </div>
                    {prepaidBalance.totalBalance >= (order.total_price || 0) && (
                      <i className="ri-arrow-right-line text-purple-400"></i>
                    )}
                  </div>
                </button>

                <button
                  onClick={handleChooseCashPayment}
                  disabled={isProcessingPrepaid}
                  className="w-full p-4 rounded-lg border-2 border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <i className="ri-money-dollar-circle-line text-xl text-gray-600"></i>
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-semibold text-gray-900">現場付款</div>
                      <div className="text-sm text-gray-500">
                        客戶將於取件時以現金或其他方式付款
                      </div>
                    </div>
                    <i className="ri-arrow-right-line text-gray-400"></i>
                  </div>
                </button>
              </div>

              <button
                onClick={() => setShowPrepaidPaymentModal(false)}
                disabled={isProcessingPrepaid}
                className="w-full mt-4 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
            </div>
          </div>
        )}

        {/* ------------------- Modal: 日曆同步 ------------------- */}
        {showCalendarSyncModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <i className="ri-calendar-event-line text-2xl text-amber-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">此訂單已同步至 Google 日曆</h3>
              </div>
              <p className="text-gray-600 mb-2">此訂單已經同步過 Google 日曆事件。</p>
              <p className="text-gray-600 mb-4">如果訂單資訊有修改（如日期、器材等），建議重新同步日曆：</p>
              <ul className="text-sm text-gray-600 mb-6 space-y-1 bg-gray-50 rounded-lg p-4">
                <li className="flex items-center gap-2">
                  <i className="ri-delete-bin-line text-red-500"></i> 刪除舊的日曆事件
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-add-circle-line text-emerald-500"></i> 建立新的日曆事件（包含最新資訊）
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-information-line text-blue-500"></i> 事件 ID：訂單 {order.order_number}
                </li>
              </ul>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCalendarSyncModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  取消
                </button>
                <button
                  onClick={() => navigate(adminOrderContractPath(order.id) ?? '/admin/orders')}
                  className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  不更新，直接前往
                </button>
                <button
                  onClick={() => syncCalendarAndNavigate(true)}
                  disabled={isSyncingCalendar}
                  className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {isSyncingCalendar ? '同步中...' : '重新同步'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ------------------- Modal: 重新簽署警告（保留） ------------------- */}
        {showResignWarning && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <i className="ri-alert-line text-2xl text-amber-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">此訂單已簽署過合約</h3>
              </div>
              <p className="text-gray-600 mb-2">此訂單已經產生過合約並同步至 Google 日曆。</p>
              <p className="text-gray-600 mb-4">如果重新簽署，系統將會：</p>
              <ul className="text-sm text-gray-600 mb-6 space-y-1">
                <li className="flex items-center gap-2">
                  <i className="ri-delete-bin-line text-red-500"></i> 刪除舊的合約
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-add-circle-line text-emerald-500"></i> 建立新的合約
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-file-copy-line text-blue-500"></i> 產生新的合約記錄
                </li>
              </ul>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowResignWarning(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  取消
                </button>
                <button
                  onClick={() => {
                    setShowResignWarning(false);
                    navigate(adminOrderContractPath(order.id, 'resync=true') ?? '/admin/orders');
                  }}
                  className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  確認重新簽署
                </button>
              </div>
            </div>
          </div>
        )}

        {showOverrideModal && (
          <ManagerOverrideModal
            isOpen={showOverrideModal}
            onClose={() => { setShowOverrideModal(false); setPendingStatusChange(null); }}
            onConfirm={handleOverrideConfirm}
            scenario={overrideScenario}
            orderNumber={order?.order_number}
          />
        )}

        {/* 主要內容區塊 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 左側：訂單資訊 */}
          <div className="lg:col-span-2 space-y-6">
            {/* 客戶資訊 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-user-line text-teal-600"></i>
                客戶資訊
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500 mb-1">姓名</div>
                  <div className="text-base font-medium text-gray-900">{customer?.name || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">公司</div>
                  <div className="text-base font-medium text-gray-900">{customer?.company || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">電子郵件</div>
                  <div className="text-base font-medium text-gray-900">{customer?.email || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">電話</div>
                  <div className="text-base font-medium text-gray-900">{customer?.phone || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">客戶折扣</div>
                  <div className="text-base font-medium text-emerald-600">
                    {customer?.discount ? `${customer.discount}%` : '無折扣'}
                  </div>
                </div>
              </div>
            </div>

            {/* 租借資訊 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-calendar-line text-teal-600"></i>
                租借資訊
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500 mb-1">開始日期</div>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editedOrder.start_date || ''}
                      onChange={e => setEditedOrder({ ...editedOrder, start_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  ) : (
                    <div className="text-base font-medium text-gray-900">
                      {new Date(order.start_date).toLocaleDateString('zh-TW')}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">結束日期</div>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editedOrder.end_date || ''}
                      onChange={e => setEditedOrder({ ...editedOrder, end_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  ) : (
                    <div className="text-base font-medium text-gray-900">
                      {new Date(order.end_date).toLocaleDateString('zh-TW')}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">取件門市</div>
                  {isEditing ? (
                    <select
                      value={editedOrder.pickup_location_id || ''}
                      onChange={e => setEditedOrder({ ...editedOrder, pickup_location_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">請選擇門市</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name_zh}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <>
                      <div className="text-base font-medium text-gray-900">
                        <i className="ri-map-pin-line text-emerald-600 mr-1"></i>
                        {order.pickup_location?.name_zh}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{order.pickup_location?.address}</div>
                    </>
                  )}
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">還車門市</div>
                  {isEditing ? (
                    <select
                      value={editedOrder.return_location_id || ''}
                      onChange={e => setEditedOrder({ ...editedOrder, return_location_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">請選擇門市</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name_zh}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <>
                      <div className="text-base font-medium text-gray-900">
                        <i className="ri-map-pin-line text-red-600 mr-1"></i>
                        {order.return_location?.name_zh}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{order.return_location?.address}</div>
                    </>
                  )}
                </div>
                <div className="col-span-2">
                  <div className="text-sm text-gray-500 mb-1">訂單狀態</div>
                  {isEditing ? (
                    <div>
                      <select
                        value={editedOrder.status || ''}
                        onChange={e => setEditedOrder({ ...editedOrder, status: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                      >
                        <option value="pending">已送出，待門市確認</option>
                        {/* Phase1104: approved = accepted, NOT ready_for_pickup */}
                        <option value="approved">訂單已接受（非器材備妥）</option>
                        <option value="renting">已取件 / 租借中（契約已生效）</option>
                        <option value="need_info">需補充資料 / 審核中</option>
                        <option value="completed">已歸還 / 已完成</option>
                        {order.status !== 'renting' && <option value="cancelled">已取消</option>}
                      </select>
                      {order.status === 'renting' && (
                        <p className="text-xs text-orange-600 mt-1.5 flex items-center gap-1">
                          <i className="ri-shield-check-line"></i>
                          契約生效中，不可普通取消。請走「已歸還/完成」或「需補充資料/審核」流程處理。
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadgeClass(order.status)}`}>
                      {getStatusText(order.status)}
                    </span>
                  )}
                </div>

                {/* ⏰ Phase4a — 取件/歸還時間 */}
                <div className="col-span-2 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <i className="ri-time-line text-teal-600"></i>
                      取件 / 歸還時間
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Requested Times */}
                    <div>
                      <div className="text-sm text-gray-500 mb-1">客戶期望取件時間</div>
                      <div className="text-base font-medium text-gray-900">
                        {order.requested_pickup_time || (
                          <span className="text-gray-400 text-sm">未填寫</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">客戶期望歸還時間</div>
                      <div className="text-base font-medium text-gray-900">
                        {order.requested_return_time || (
                          <span className="text-gray-400 text-sm">未填寫</span>
                        )}
                      </div>
                    </div>
                    {/* Confirmed Times */}
                    <div>
                      <div className="text-sm text-gray-500 mb-1">門市確認取件時間</div>
                      {order.confirmed_pickup_time ? (
                        <span className="flex items-center gap-1">
                          <i className="ri-checkbox-circle-line text-emerald-500"></i>
                          {order.confirmed_pickup_time}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">待確認</span>
                      )}
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">門市確認歸還時間</div>
                      {order.confirmed_return_time ? (
                        <span className="flex items-center gap-1">
                          <i className="ri-checkbox-circle-line text-emerald-500"></i>
                          {order.confirmed_return_time}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">待確認</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* 加租車輛確認區塊 */}
                {hasVehicleRequest && (
                  <div className="col-span-2 pt-4 border-t border-gray-200">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <div className="text-sm text-gray-500 mb-2">加租車輛狀態</div>
                        {isEditing ? (
                          <div className="space-y-3">
                            <label className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={vehicleConfirmed}
                                onChange={e => {
                                  setVehicleConfirmed(e.target.checked);
                                  if (!e.target.checked) {
                                    setSelectedVehicleId('');
                                  }
                                }}
                                className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer"
                              />
                              <div>
                                <div className="text-base font-medium text-gray-900">
                                  確認可提供車輛
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {vehicleConfirmed 
                                    ? '✓ 接受訂單，請選擇要指派的車輛' 
                                    : '✗ 取消勾選表示車輛滿檔，訂單不含車輛費用'}
                                </div>
                              </div>
                            </label>
                            
                            {/* 車輛選擇下拉選單 */}
                            {vehicleConfirmed && (
                              <div className="ml-8 space-y-2">
                                <label className="text-sm font-medium text-gray-700">指派車輛</label>
                                <select
                                  value={selectedVehicleId}
                                  onChange={e => setSelectedVehicleId(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                                >
                                  <option value="">請選擇車輛</option>
                                  {vehicles.map(vehicle => (
                                    <option key={vehicle.id} value={vehicle.id}>
                                      {vehicle.plate_number} - 日租 NT$ {vehicle.rental_fee.toLocaleString()} / 成本 NT$ {vehicle.cost.toLocaleString()}
                                      {vehicle.status !== 'available' ? ` (${vehicle.status === 'rented' ? '出租中' : '維修中'})` : ''}
                                    </option>
                                  ))}
                                </select>
                                
                                {/* 選中車輛的費用預覽 */}
                                {selectedVehicle && (
                                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                    <div className="flex items-center justify-between text-sm">
                                      <span className="text-blue-700">車牌號碼</span>
                                      <span className="font-semibold text-blue-900">{selectedVehicle.plate_number}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm mt-1">
                                      <span className="text-blue-700">日租金</span>
                                      <span className="font-semibold text-blue-900">NT$ {selectedVehicle.rental_fee.toLocaleString()}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm mt-1">
                                      <span className="text-blue-700">租借天數</span>
                                      <span className="font-semibold text-blue-900">{calcRentalDays(editedOrder.start_date || order.start_date, editedOrder.end_date || order.end_date)} 天</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-blue-200">
                                      <span className="text-blue-700 font-medium">車輛費用小計</span>
                                      <span className="font-bold text-blue-900">
                                        NT$ {(selectedVehicle.rental_fee * calcRentalDays(editedOrder.start_date || order.start_date, editedOrder.end_date || order.end_date)).toLocaleString()}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs mt-2 pt-2 border-t border-blue-200">
                                      <span className="text-amber-700">車輛成本（本票用）</span>
                                      <span className="font-semibold text-amber-800">NT$ {selectedVehicle.cost.toLocaleString()}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              {vehicleConfirmed ? (
                                <>
                                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-emerald-100 text-emerald-800">
                                    <i className="ri-checkbox-circle-line mr-1"></i>
                                    已確認
                                  </span>
                                  <span className="text-sm text-gray-600">
                                    車輛費用：NT$ {order.vehicle_fee ? order.vehicle_fee.toLocaleString() : '0'}
                                  </span>
                                </>
                              ) : (
                                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                                  <i className="ri-close-circle-line mr-1"></i>
                                  車輛滿檔
                                </span>
                              )}
                            </div>
                            {/* 顯示已指派的車輛資訊 */}
                            {vehicleConfirmed && order.vehicle_id && (
                              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                {(() => {
                                  const assignedVehicle = vehicles.find(v => v.id === order.vehicle_id);
                                  if (!assignedVehicle) return <span className="text-gray-500 text-sm">車輛資料載入中...</span>;
                                  return (
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <i className="ri-car-line text-blue-600"></i>
                                        <span className="font-semibold text-blue-900">{assignedVehicle.plate_number}</span>
                                      </div>
                                      <div className="text-xs text-blue-700">
                                        日租金 NT$ {assignedVehicle.rental_fee.toLocaleString()} × {calcRentalDays(order.start_date, order.end_date)} 天
                                      </div>
                                      <div className="text-xs text-amber-700">
                                        車輛成本（本票用）：NT$ {assignedVehicle.cost.toLocaleString()}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-car-line text-2xl text-blue-600"></i>
                      </div>
                    </div>
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-600">
                        <i className="ri-information-line mr-1"></i>
                        客人已在訂單中要求加租車輛（油錢、過路費另計）
                      </div>
                    </div>
                  </div>
                )}

                <div className="col-span-2">
                  <div className="text-sm text-gray-500 mb-1">備註</div>
                  {isEditing ? (
                    <textarea
                      value={editedOrder.notes || ''}
                      onChange={e => setEditedOrder({ ...editedOrder, notes: e.target.value })}
                      rows={3}
                      maxLength={500}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="輸入備註..."
                    />
                  ) : (
                    <div className="text-base text-gray-900 whitespace-pre-wrap">{order.notes || '-'}</div>
                  )}
                </div>
              </div>
            </div>

            <PickupGovernanceChecklist visible={order.status === 'approved'} />

            {/* 器材清單 - 完整版本從 863 恢復 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-list-check text-teal-600"></i>
                器材清單
              </h2>
              <div className="space-y-3">
                {orderItems.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <i className="ri-list-check text-xl text-gray-600"></i>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 flex items-center gap-2">
                          {item.product?.name_zh || '未知器材'}
                          {item.is_urent_item && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                              Urent
                            </span>
                          )}
                          {item.is_external_dispatch && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                              外調
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {item.daily_price_snapshot ? `${item.daily_price_snapshot.toLocaleString()} 元/天` : '未設定每日價格'}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">{formatCurrency(item.subtotal)}</div>
                      <div className="text-sm text-gray-500">
                        {item.quantity} 件 × {item.daily_price_snapshot ? `${item.daily_price_snapshot.toLocaleString()} 元/天` : '未設定每日價格'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 器材分配 */}
            <AllocationSection
              orderId={order.id}
              orderItems={orderItems}
              startDate={order.start_date}
              endDate={order.end_date}
              onAllocationChange={handleAllocationChange}
            />
          </div>

          {/* 右側：摘要與日誌 */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-6">
              {costSummary && <CostSummaryCard summary={costSummary} isAdmin />}
              {/* Phase4a — Payable Summary (read-only, sole source: EF) */}
              <PayableSummaryCard orderId={order.id} />
              <OrderLogSection orderId={order.id} refreshKey={logRefreshKey} />
              <AuditTimeline orderId={order.id} currentStatus={order.status} createdAt={order.created_at} />
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-xs text-gray-500">
                  <div>建立時間：{new Date(order.created_at).toLocaleString('zh-TW')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
